import React, { useEffect, useState } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

// Two-mode PayPal card: user picks Subscription (recurring) or One-time,
// then a PayPal Smart Button renders below with the chosen intent.
// PayPal's JS SDK cannot host both intents at once so we swap providers.
//
// Client ID + plan IDs come from /api/paypal/config which is env-aware on the
// backend (Sandbox vs Live). This means the frontend never has to know which
// environment is active — one source of truth.

export default function PayPalCheckout({ interval, amount }) {
  const { refreshUser } = useAuth();
  const [mode, setMode] = useState("subscription"); // "subscription" | "onetime"
  const [config, setConfig] = useState(null); // { client_id, monthly_plan_id, yearly_plan_id, env, configured }
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/paypal/config");
        setConfig(data);
        setFailed(!data.configured);
      } catch (e) {
        setFailed(true);
      }
    })();
  }, []);

  if (failed) {
    return (
      <div className="nb-card p-3 text-xs font-bold text-[#4a4a4a]" data-testid="paypal-not-configured">
        PayPal not fully configured yet — contact support.
      </div>
    );
  }
  if (!config) return null;

  const planId = interval === "yearly" ? config.yearly_plan_id : config.monthly_plan_id;

  // Key changes when mode/interval changes so PayPalScriptProvider re-mounts
  // with the correct intent (SDK caches the script per config).
  const providerKey = `${mode}-${interval}-${config.env}`;
  const sdkOptions = mode === "subscription"
    ? {
        "client-id": config.client_id,
        currency: "USD",
        vault: true,
        intent: "subscription",
      }
    : {
        "client-id": config.client_id,
        currency: "USD",
        intent: "capture",
      };

  return (
    <div
      className="nb-card p-4 space-y-3"
      style={{ background: "#fdecc8" }}
      data-testid="paypal-checkout-card"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="font-display font-black text-sm">Or pay with PayPal</div>
        <div className="flex gap-1" data-testid="paypal-mode-toggle">
          <button
            data-testid="paypal-mode-subscription"
            onClick={() => setMode("subscription")}
            className={`chip !text-[10px] !py-0.5 !px-2 ${mode === "subscription" ? "active" : ""}`}
          >
            Auto-renew
          </button>
          <button
            data-testid="paypal-mode-onetime"
            onClick={() => setMode("onetime")}
            className={`chip !text-[10px] !py-0.5 !px-2 ${mode === "onetime" ? "active" : ""}`}
          >
            One-time
          </button>
        </div>
      </div>
      <div className="text-[11px] font-bold text-[#4a4a4a]">
        {mode === "subscription"
          ? `$${amount} / ${interval === "yearly" ? "year" : "month"} · cancel anytime from your PayPal account`
          : `$${amount} one-time · single charge, no auto-renew`}
      </div>
      <div data-testid="paypal-buttons-container">
        <PayPalScriptProvider key={providerKey} options={sdkOptions}>
          {mode === "subscription" ? (
            <PayPalButtons
              key={`sub-${interval}`}
              style={{ layout: "vertical", label: "subscribe", shape: "rect" }}
              createSubscription={(_data, actions) =>
                actions.subscription.create({ plan_id: planId })
              }
              onApprove={async ({ subscriptionID }) => {
                try {
                  await api.post("/paypal/subscriptions/record", {
                    subscription_id: subscriptionID,
                    plan: interval,
                  });
                  toast.success("🎉 Pro activated via PayPal!");
                  await refreshUser();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Could not activate — please contact support");
                }
              }}
              onCancel={() => toast.info("PayPal checkout cancelled")}
              onError={(err) => {
                console.error("PayPal subscription error", err);
                toast.error("PayPal error — please try again");
              }}
            />
          ) : (
            <PayPalButtons
              key={`ot-${interval}`}
              style={{ layout: "vertical", label: "pay", shape: "rect" }}
              createOrder={async () => {
                const { data } = await api.post("/paypal/orders", { plan: interval });
                return data.id;
              }}
              onApprove={async ({ orderID }) => {
                try {
                  await api.post(`/paypal/orders/${orderID}/capture`);
                  toast.success("🎉 Pro activated via PayPal!");
                  await refreshUser();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Capture failed — contact support");
                }
              }}
              onCancel={() => toast.info("PayPal checkout cancelled")}
              onError={(err) => {
                console.error("PayPal order error", err);
                toast.error("PayPal error — please try again");
              }}
            />
          )}
        </PayPalScriptProvider>
      </div>
    </div>
  );
}
